// #region imports

// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { Observable, BehaviorSubject, Subject, Subscription } from 'rxjs';

// HS Code
import { Constants, LanguageModel, SelectListItem } from '../../../models';
import { Utils } from '../../../shared';

// #endregion

@Injectable({
  providedIn: 'root'
})
export abstract class LanguagesBaseService {
  protected _isInitialised: boolean;
  protected _initialisedLocale: string;

  protected _languages$: BehaviorSubject<LanguageModel[]>;
  get languages$(): Observable<LanguageModel[]> {
    return this._languages$.asObservable();
  }

  protected _languagesList$: BehaviorSubject<SelectListItem[] | null>;
  get languagesList$(): Observable<SelectListItem[] | null> {
    return this._languagesList$.asObservable();
  }

  protected _selectedLanguage$: BehaviorSubject<LanguageModel>;
  get selectedLanguage$(): Observable<LanguageModel> {
    return this._selectedLanguage$.asObservable();
  }

  constructor() {
    this._isInitialised = false;
    this._initialisedLocale = Constants.DEFAULT_LOCALE;

    this._languages$ = new BehaviorSubject<LanguageModel[]>(new Array<LanguageModel>());
    this._languagesList$ = new BehaviorSubject<SelectListItem[] | null>(null);
    this._selectedLanguage$ = new BehaviorSubject<LanguageModel>(Constants.DEFAULT_LANGUAGE);
  }

  abstract initialiseLanguage(selectedLocale: string | undefined): void;
  abstract loadLanguages(): void;
  abstract updateUserLanguage(userLanguage: LanguageModel, surveyId?: string): void;

  protected _getLocale(language: LanguageModel): string
  {
    const locale = language.cultureCode ?
    `${language.isoTwoLetterName}-${language.cultureCode}` :
    `${language.isoTwoLetterName}`;

    return locale;
  }

  protected _getSavedLanguage(): LanguageModel | null {
    const storedLanguage = localStorage.getItem(Constants.SELECTED_LANGUAGE_LOCAL_STORAGE_KEY);
    if (!Utils.isNull(storedLanguage)) {
      try {
        const savedLanguage = JSON.parse(
          storedLanguage as string
        );

        if (!Utils.isUndefined(savedLanguage)) {
          return savedLanguage;
        }
      } catch (err) {
        return null;
      }
    }

    return null;
  }

  protected _setSavedLanguage(languageToStore: LanguageModel): void {
    const serializedLanguage = JSON.stringify(languageToStore);
    localStorage.setItem(Constants.SELECTED_LANGUAGE_LOCAL_STORAGE_KEY, serializedLanguage);
  }

  protected _processLanguages(languages: LanguageModel[]) {
    this._createSelectListItemArrayForLanguageDropDown(languages);
    this._setSelectedLanguageFromSelectedLocale(languages);
  }

  private _createSelectListItemArrayForLanguageDropDown(languages: LanguageModel[]): void {
    const languageList = new Array<SelectListItem>();
    languages.forEach(l => {
      const selectedId = this._selectedLanguage$.value && this._selectedLanguage$.value.id;
      const selectListItem = {
        text: l.nativeName,
        value: l.id.toString(),
        isSelected: l.id === selectedId
      } as SelectListItem;
      languageList.push(selectListItem);
    });
    this._languagesList$.next(languageList);
  }

  private _setSelectedLanguageFromSelectedLocale(languages: LanguageModel[]) {
    if (!Utils.isNull(languages)) {
      let selectedLanguage = languages.find(l => {
        return this._getLocale(l) === this._initialisedLocale;
      });

      if (Utils.isNull(selectedLanguage) || Utils.isUndefined(selectedLanguage)) {
        languages.find(l => {
          return this._getLocale(l) === Constants.DEFAULT_LOCALE;
        });
      }

      if (!Utils.isUndefined(selectedLanguage) && !Utils.isNull(selectedLanguage)) {
        this._isInitialised = true;
        this._initialisedLocale = this._getLocale(selectedLanguage as LanguageModel);
        localStorage.setItem(
          Constants.SELECTED_LANGUAGE_LOCAL_STORAGE_KEY,
          JSON.stringify(selectedLanguage as LanguageModel)
        );
        this._selectedLanguage$.next(selectedLanguage as LanguageModel);
      }
    }
  }
}
